import type { Map as MLMap } from 'maplibre-gl';
import type { VectorHighlightTarget } from '@/components/vector/vectorIdentifyHighlight';
import type { PolygonSymbolState } from '@/components/vector/polygonSymbolRegistry';
import type { UniqueValueSymbology } from '@/components/vector/vectorStyleModel';
import type { LineSymbolState } from '@/components/vector/lineSymbolRegistry';
import type { VectorEditResult } from '@/components/vector/vectorEditSession';

/**
 * Shared container key used to expose the mounted MapLibre instance so
 * interleaved secondary adapters (deck.gl) can attach to the same map.
 */
export const MAPLIBRE_HOST_HANDLE_KEY = '__geosoft_maplibre_instance';
const MAPLIBRE_ADAPTER_KEY = '__geosoft_maplibre_adapter';

/** Minimal surface ToolCards need — avoids a circular import with MapLibreAdapter. */
export type MapLibreRasterToolCardAdapter = {
  /** Live TiTiler XYZ template after mount / DEM colorize refinements. */
  getRasterTileTemplate?(layerId: string): string | null;
  setVectorPaint?(
    layerId: string,
    delta: {
      readonly fillColor?: string;
      readonly fillOpacity?: number;
      readonly lineColor?: string;
      readonly lineWidth?: number;
      readonly lineSymbol?: LineSymbolState;
      readonly pointSize?: number;
      readonly pointSymbolId?: string;
      readonly polygonSymbol?: PolygonSymbolState;
      readonly uniqueValueSymbology?: UniqueValueSymbology | null;
    },
  ): void;
  setVectorHighlight?(layerId: string, featureId: VectorHighlightTarget): void;
  zoomToVectorBounds?(bounds: readonly [number, number, number, number]): void;
  setIdentifyCursor?(armed: boolean): void;
  setVectorEditMode?(
    layerId: string,
    tool: 'draw' | 'modify' | 'scale' | 'delete' | 'trace' | null,
    geometryKind?: 'point' | 'line' | 'polygon' | 'unknown',
    features?: ReadonlyArray<{
      readonly id?: string | number;
      readonly properties?: Record<string, unknown>;
      readonly geometry?: unknown;
    }>,
    selectedFeatureId?: string | number | null,
    onEdit?: (result: VectorEditResult) => void,
  ): void;
  deleteSelectedVectorFeature?(
    layerId: string,
    selectedFeatureId?: string | number | null,
    onEdit?: (result: VectorEditResult) => void,
  ): void;
  removeVectorSessionFeature?(layerId: string, featureId: string | number): void;
  setVectorEditCursor?(cursor: string): void;
  applyRasterToolCard(
    layerId: string,
    state: {
      readonly opacity: number;
      readonly brightness: number;
      readonly contrast: number;
      readonly saturation: number;
      readonly bands: readonly [number, number, number];
      readonly colorRamp: string;
      readonly noDataMask: boolean;
    },
    options?: {
      readonly multiBandImagery?: boolean;
      readonly nodata?: number | null;
      readonly bandCount?: number;
      readonly preserveColormap?: boolean;
      readonly preserveNodata?: boolean;
    },
  ): void;
};

export function setMapLibreHostHandle(container: HTMLElement, map: MLMap): void {
  (container as unknown as Record<string, unknown>)[MAPLIBRE_HOST_HANDLE_KEY] = map;
}

export function getMapLibreHostHandle(container: HTMLElement): MLMap | null {
  const value = (container as unknown as Record<string, unknown>)[MAPLIBRE_HOST_HANDLE_KEY];
  return value ? (value as MLMap) : null;
}

export function clearMapLibreHostHandle(container: HTMLElement): void {
  delete (container as unknown as Record<string, unknown>)[MAPLIBRE_HOST_HANDLE_KEY];
  delete (container as unknown as Record<string, unknown>)[MAPLIBRE_ADAPTER_KEY];
}

/** Expose the owning adapter so ToolCards can call typed apply methods. */
let lastMapLibreAdapter: MapLibreRasterToolCardAdapter | null = null;

export function setMapLibreAdapterHandle(
  container: HTMLElement,
  adapter: MapLibreRasterToolCardAdapter,
): void {
  lastMapLibreAdapter = adapter;
  (container as unknown as Record<string, unknown>)[MAPLIBRE_ADAPTER_KEY] = adapter;
}

export function getMapLibreAdapterHandle(
  container: HTMLElement | null,
): MapLibreRasterToolCardAdapter | null {
  let node: HTMLElement | null = container;
  while (node) {
    const value = (node as unknown as Record<string, unknown>)[MAPLIBRE_ADAPTER_KEY];
    if (value) return value as MapLibreRasterToolCardAdapter;
    node = node.parentElement;
  }
  return lastMapLibreAdapter;
}
